/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import PropTypes from 'prop-types';
import { Activity, Glasses, PhoneOff, Radio, RefreshCw, Video } from 'lucide-react';
import Modal from './Modal';
import LiveSessionServerNotice from './LiveSessionServerNotice';

const PLATFORM_LABELS = {
  xreal: 'XREAL',
  meta: 'Meta',
  snap: 'Snap',
  optaric: 'Optaric',
  samsung: 'Samsung',
};

const isJoinable = (session) => session.status !== 'in_live_session';
const getPlatformLabel = (session) => PLATFORM_LABELS[session.app_platform] || 'Other';
const getDisplayName = (session) => session.creator_name || session.device_name || session.room;

const LiveSessionsEmptyState = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4">
    {/* Animated Radar / Broadcast Beacon */}
    <div className="relative flex items-center justify-center mb-5">
      <div className="absolute w-28 h-28 rounded-full bg-primary/5 animate-ping opacity-75 pointer-events-none" />
      <div className="absolute w-20 h-20 rounded-full bg-primary/10 animate-pulse pointer-events-none" />
      <div className="relative w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-inner z-10">
        <Radio size={30} className="text-primary animate-pulse" />
      </div>
    </div>

    {/* Live Status Pill */}
    <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-base-200 border border-base-content/10 text-[11px] font-bold text-base-content/70 shadow-sm mb-4">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span className="uppercase tracking-wider">Listening for Broadcasts</span>
    </div>

    {/* Heading & Subtitle */}
    <h3 className="text-xl sm:text-2xl font-black text-base-content tracking-tight mb-2">
      No Active Live Sessions
    </h3>
    <p className="text-xs sm:text-sm text-base-content/60 font-medium max-w-md mx-auto leading-relaxed mb-6">
      There are currently no active video streams being broadcast. When a technician or field device starts streaming, it will automatically appear here in real time.
    </p>

    {/* 3-Step Information Guidance Cards */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full max-w-2xl text-left mb-6">
      <div className="p-3.5 rounded-2xl bg-base-200/60 border border-base-content/5 flex flex-col gap-2 hover:bg-base-200 transition-colors">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Glasses size={16} />
        </div>
        <div>
          <h4 className="text-xs font-black text-base-content uppercase tracking-wider">1. Start Broadcast</h4>
          <p className="text-[11px] text-base-content/60 font-medium leading-normal mt-0.5">
            Technicians launch the live session on smart glasses.
          </p>
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-base-200/60 border border-base-content/5 flex flex-col gap-2 hover:bg-base-200 transition-colors">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Activity size={16} />
        </div>
        <div>
          <h4 className="text-xs font-black text-base-content uppercase tracking-wider">2. Auto Detected</h4>
          <p className="text-[11px] text-base-content/60 font-medium leading-normal mt-0.5">
            Active sessions appear instantly with room details and platform tags.
          </p>
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-base-200/60 border border-base-content/5 flex flex-col gap-2 hover:bg-base-200 transition-colors">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Video size={16} />
        </div>
        <div>
          <h4 className="text-xs font-black text-base-content uppercase tracking-wider">3. Instant Join</h4>
          <p className="text-[11px] text-base-content/60 font-medium leading-normal mt-0.5">
            Connect directly with two-way audio, remote video, and live recording.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const LiveSessionsPanel = ({
  isOpen,
  sessions,
  joinedRoom = null,
  serverStatus = 'connected',
  onReconnect,
  reconnectAttemptsLeft = 0,
  onJoin,
  onLeave,
  onClose,
}) => {
  const disconnected = serverStatus === 'disconnected';
  const reconnecting = serverStatus === 'reconnecting';
  const showNotice = disconnected || (reconnecting && sessions.length === 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Live Sessions" className="w-[75vw] max-w-[75vw] h-[75vh] max-h-[75vh]">
      <div className="h-full flex flex-col">
        {showNotice && (
          <LiveSessionServerNotice
            status={serverStatus}
            onReconnect={onReconnect}
            reconnectAttemptsLeft={reconnectAttemptsLeft}
          />
        )}

        {!showNotice && sessions.length === 0 && (
          <LiveSessionsEmptyState />
        )}

        {!showNotice && sessions.length > 0 && (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-base-content/10 flex-shrink-0">
              {reconnecting ? (
                <div className="flex items-center gap-2 text-warning">
                  <RefreshCw size={14} className="animate-spin" />
                  <span className="text-xs font-black uppercase tracking-wider">
                    Reconnecting to live session server…
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                  <span className="text-xs font-black uppercase tracking-wider text-base-content">
                    {sessions.length} Active {sessions.length === 1 ? 'Session' : 'Sessions'}
                  </span>
                </div>
              )}
              <span className="text-[11px] font-bold text-base-content/40 uppercase tracking-widest">
                Live Stream Feed
              </span>
            </div>

            <div className="flex-1 overflow-y-auto flex flex-col gap-3 pr-1">
              {sessions.map((session) => {
                const joinable = isJoinable(session);
                const isJoined = joinedRoom === session.room;
                const disableJoin = !isJoined && (joinedRoom || !joinable);
                const blocked = !isJoined && !joinable;

                return (
                  <div
                    key={session.room}
                    className={`flex items-center px-4 py-4 rounded-2xl border border-base-300 transition-all gap-4 ${blocked ? 'cursor-not-allowed' : 'hover:bg-base-200'}`}
                  >
                    <div className="flex flex-col min-w-0 flex-shrink-0 w-40">
                      {session.app_name ? (
                        <span className="badge badge-ghost badge-sm font-bold truncate w-fit">
                          {session.app_name}
                        </span>
                      ) : <span />}
                      <span className="badge badge-outline badge-sm font-bold w-fit mt-1">
                        {getPlatformLabel(session)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${joinable ? 'bg-success' : 'bg-warning'}`} />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-black uppercase tracking-tight text-base-content truncate">
                          {getDisplayName(session)}
                        </span>
                        <span className="text-[11px] font-medium text-base-content/50 uppercase tracking-wide truncate">
                          Room {session.room}
                        </span>
                      </div>
                    </div>

                    <div className="flex-shrink-0 w-40 text-[11px] font-medium text-base-content/50 truncate">
                      {!joinable && session.viewer_name ? `Viewer: ${session.viewer_name}` : ''}
                    </div>

                    <span className={`badge badge-sm font-bold flex-shrink-0 ${joinable ? 'badge-success text-success-content' : 'badge-warning text-warning-content'}`}>
                      {joinable ? 'AVAILABLE' : 'IN CALL'}
                    </span>

                    <div className="flex-shrink-0">
                      {isJoined ? (
                        <button
                          onClick={onLeave}
                          className="btn btn-sm btn-error btn-outline flex-shrink-0 gap-1"
                          title="Leave session"
                        >
                          <PhoneOff size={14} />
                          Leave
                        </button>
                      ) : (
                        <button
                          onClick={() => onJoin(session)}
                          disabled={disableJoin}
                          className={`btn btn-sm btn-primary flex-shrink-0 disabled:opacity-30 ${blocked ? 'cursor-not-allowed' : ''}`}
                          title={
                            disableJoin
                              ? (!joinable ? 'Session already in progress' : 'Leave your current session first')
                              : 'Join session'
                          }
                        >
                          Join
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

LiveSessionsPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  sessions: PropTypes.arrayOf(PropTypes.object).isRequired,
  joinedRoom: PropTypes.string,
  serverStatus: PropTypes.string,
  onReconnect: PropTypes.func,
  reconnectAttemptsLeft: PropTypes.number,
  onJoin: PropTypes.func.isRequired,
  onLeave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LiveSessionsPanel;
